import React, { useEffect, useState } from "react";
import axios from "axios";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { t } from "i18next";
import {
  createCategory,
  getCategory,
  updateCategory,
  getSystemVehicleTypes,
} from "services/customAPI";
import { DTO, Types } from "shared-lib";
import { errorToast, successToast } from "utils/commons";
import Loader from "components/loader/loader";

type VehicleTypeOption = {
  id: number;
  type: string;
  label: string;
  active: boolean;
  allowedTransportModes?: string[];
};

type CategoryFormValues = Omit<DTO.CategoryDTO, 'vehicleTypeId' | 'transportMode'> & {
  transportMode?: string;
  vehicleTypeId?: number | string;
};

const CategorySchema = (isEditing: boolean) => {
  return Yup.object().shape({
    name: Yup.string().required(t("nameIsRequired")),
    multiply: Yup.number()
      .required(t("multiplyIsRequired"))
      .positive(t("shouldPositive")),
    urlImg: Yup.mixed()
      .test("fileRequired", t("urlImgIsRequired"), function (value) {
        if (isEditing && typeof value === "string") return true;
        return value instanceof File;
      })
      .test("fileType", t("invalidFileType"), function (value) {
        if (isEditing && typeof value === "string") return true;
        if (!value) return true;
        return (
          value instanceof File &&
          ["image/jpeg", "image/png", "image/jpg"].includes(value.type)
        );
      }),
    transportMode: Yup.string()
      .oneOf(Object.values(Types.TransportMode), t("Selecione um modo de transporte válido"))
      .required(t("Modo de transporte é obrigatório")),
    vehicleTypeId: Yup.number()
      .required(t("Tipo de veículo é obrigatório"))
      .positive(t("Selecione um tipo de veículo")),
  });
};

const resolveCategoryErrorMessage = (error: unknown, fallback: string) => {
  if (
    axios.isAxiosError(error) &&
    error.response?.data &&
    typeof error.response.data === "object" &&
    "message" in error.response.data
  ) {
    const message = (error.response.data as { message?: unknown }).message;
    if (typeof message === "string" && message.trim().length > 0) {
      return message;
    }
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallback;
};

function CreateCategory() {
  const [category, setCategory] = useState<CategoryFormValues>();
  const [isLoading, setIsLoading] = useState(false);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeOption[]>([]);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const [existingImage, setExistingImage] = useState<string | null>(null);

  // Load vehicle types for dropdown
  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        const res = await getSystemVehicleTypes();
        const payload = res?.data ?? res;
        const types = (payload?.data ?? payload ?? []).filter(
          (vt: VehicleTypeOption) => vt.active
        );
        setVehicleTypes(types);
      } catch {
        // Non-blocking — dropdown will just be empty
      }
    };
    fetchVehicleTypes();
  }, []);

  useEffect(() => {
    const fetchCategory = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const response = await getCategory(Number(id));
        const data = response?.data?.data ?? response?.data;
        if (data) {
          setExistingImage(data.urlImg || null);
          setCategory({
            id: data.id,
            name: data.name,
            multiply: parseFloat(data.multiply),
            urlImg: data.urlImg ? data.urlImg : null,
            active: data.active,
            transportMode: data.transportMode || "",
            vehicleTypeId: data.vehicleTypeId || "",
          });
        }
      } catch (error) {
        errorToast("Erro ao carregar categoria");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategory();
  }, [id]);

  const renderForm = (initialValues: CategoryFormValues) => (
    <Formik<CategoryFormValues>
      enableReinitialize
      initialValues={{
        ...initialValues,
        urlImg: isEditing && existingImage ? existingImage : null,
        transportMode: initialValues.transportMode || "",
        vehicleTypeId: initialValues.vehicleTypeId || "",
      }}
      validationSchema={CategorySchema(isEditing)}
      onSubmit={async (values, { setSubmitting }) => {
        const formData = new FormData();
        formData.append("name", values.name || "");
        formData.append("multiply", String(values.multiply));
        formData.append("active", String(values.active));
        if (values.transportMode) {
          formData.append("transportMode", values.transportMode);
        }
        if (values.vehicleTypeId) {
          formData.append("vehicleTypeId", String(values.vehicleTypeId));
        }
        if (values.urlImg instanceof File) {
          formData.append("urlImg", values.urlImg);
        } else if (
          isEditing &&
          values.urlImg &&
          typeof values.urlImg === "string"
        ) {
          formData.append("urlImg", values.urlImg);
        }

        try {
          if (isEditing && category?.id) {
            await updateCategory(category.id, formData);
            successToast(t("salveConfigs"));
            navigate("/admin/settings/categories");
            return;
          }
          await createCategory(formData);
          successToast(t("salveConfigs"));
          navigate("/admin/settings/categories");
        } catch (error) {
          errorToast(
            resolveCategoryErrorMessage(error, t("Erro ao criar categoria"))
          );
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({
        isSubmitting,
        setFieldValue,
        values,
        errors,
        touched,
        handleChange,
      }) => (
        <Form className="bg-white dark:bg-navy-800 p-8 rounded-lg shadow-md w-full max-w-lg space-y-4">
          <h2 className="text-2xl font-bold text-center dark:text-white">
            {t(id ? "EditarCategoria" : "CriarCategoria")}
          </h2>

          {/* Nome */}
          <div>
            <label
              htmlFor="name"
              className="block font-semibold dark:text-white"
            >
              {t("Nome")}
            </label>
            <Field
              type="text"
              name="name"
              className="w-full border dark:border-navy-600 dark:bg-navy-700 dark:text-white px-3 py-2 rounded"
            />
            <ErrorMessage
              name="name"
              component="div"
              className="text-red-500 text-sm mt-1"
            />
          </div>

          {/* Modo de Transporte */}
          <div>
            <label
              htmlFor="transportMode"
              className="block font-semibold dark:text-white"
            >
              {t("Modo de Transporte")}
            </label>
            <select
              id="transportMode"
              name="transportMode"
              value={values.transportMode || ""}
              onChange={(e) => {
                handleChange(e);
                // Reset vehicle type when transport mode changes
                setFieldValue("vehicleTypeId", "");
              }}
              className="w-full border dark:border-navy-600 dark:bg-navy-700 dark:text-white px-3 py-2 rounded"
            >
              <option value="" disabled>
                {t("Selecione...")}
              </option>
              {Object.values(Types.TransportMode).map((mode) => (
                <option key={mode} value={mode}>
                  {Types.getTransportModeEmoji(mode)} {Types.getTransportModeLabel(mode)}
                </option>
              ))}
            </select>
            {errors.transportMode && touched.transportMode && (
              <div className="text-red-500 text-sm mt-1">
                {errors.transportMode}
              </div>
            )}
          </div>

          {/* Tipo de Veículo — filtered by selected transport mode */}
          <div>
            <label
              htmlFor="vehicleTypeId"
              className="block font-semibold dark:text-white"
            >
              {t("Tipo de Veículo")}
            </label>
            <select
              id="vehicleTypeId"
              name="vehicleTypeId"
              value={values.vehicleTypeId || ""}
              disabled={!values.transportMode}
              onChange={(e) =>
                setFieldValue("vehicleTypeId", Number(e.target.value) || "")
              }
              className="w-full border dark:border-navy-600 dark:bg-navy-700 dark:text-white px-3 py-2 rounded disabled:opacity-50"
            >
              <option value="" disabled>
                {values.transportMode
                  ? t("Selecione...")
                  : t("Selecione o modo de transporte primeiro")}
              </option>
              {vehicleTypes
                .filter((vt) => {
                  if (!values.transportMode) return false;
                  const modes = vt.allowedTransportModes ?? [];
                  return modes.includes(values.transportMode);
                })
                .map((vt) => (
                  <option key={vt.id} value={vt.id}>
                    {Types.getVehicleTypeEmoji(vt.type)} {vt.label}
                  </option>
                ))}
            </select>
            {errors.vehicleTypeId && touched.vehicleTypeId && (
              <div className="text-red-500 text-sm mt-1">
                {String(errors.vehicleTypeId)}
              </div>
            )}
            {vehicleTypes.length === 0 && (
              <p className="text-xs text-gray-400 mt-1">
                {t(
                  "Nenhum tipo de veículo ativo. Ative em Configurações > Tipos de Veículos."
                )}
              </p>
            )}
          </div>

          {/* Multiplicador */}
          <div>
            <label
              htmlFor="multiply"
              className="block font-semibold dark:text-white"
            >
              {t("Multiplicador")}
            </label>
            <Field
              type="number"
              name="multiply"
              className="w-full border dark:border-navy-600 dark:bg-navy-700 dark:text-white px-3 py-2 rounded"
            />
            <ErrorMessage
              name="multiply"
              component="div"
              className="text-red-500 text-sm mt-1"
            />
          </div>

          {/* Imagem */}
          <div>
            <label
              htmlFor="urlImg"
              className="block font-semibold dark:text-white"
            >
              {t("Imagem")}
            </label>

            {/* Show existing image preview when editing */}
            {isEditing &&
              existingImage &&
              !(values.urlImg instanceof File) && (
                <div className="mb-2">
                  <img
                    src={`${process.env.REACT_APP_BASE_URL}/uploads/${existingImage}`}
                    alt="Imagem atual"
                    className="w-24 h-24 object-cover rounded mb-1"
                  />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t("Imagem atual:")} {existingImage}
                  </p>
                </div>
              )}

            <input
              id="urlImg"
              name="urlImg"
              type="file"
              accept="image/*"
              onChange={(event) => {
                if (
                  event.currentTarget.files &&
                  event.currentTarget.files[0]
                ) {
                  setFieldValue("urlImg", event.currentTarget.files[0]);
                } else if (isEditing) {
                  setFieldValue("urlImg", existingImage);
                }
              }}
              className="w-full border dark:border-navy-600 dark:bg-navy-700 dark:text-white px-3 py-2 rounded"
            />
            <ErrorMessage
              name="urlImg"
              component="div"
              className="text-red-500 text-sm mt-1"
            />

            {values.urlImg instanceof File && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t("Arquivo selecionado:")} {values.urlImg.name}
              </p>
            )}
          </div>

          {/* Active Checkbox Field */}
          <div className="mb-3 w-full">
            <div className="flex items-center gap-2">
              <input
                id="active"
                name="active"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-black focus:ring-primary"
                onChange={handleChange}
                checked={values.active}
              />
              <label
                htmlFor="active"
                className="input-custom-label !mb-0"
                style={{
                  color: document.documentElement.classList.contains("dark")
                    ? "white"
                    : "black",
                }}
              >
                {t("active")}
              </label>
            </div>
            <div className="error-input">
              {errors.active && touched.active ? errors.active : null}
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-primary text-white px-4 py-2 rounded w-full hover:opacity-90 transition disabled:opacity-50"
          >
            {isSubmitting
              ? t("Salvando...")
              : t(id ? "SalvarAlterações" : "CriarCategoria")}
          </button>
        </Form>
      )}
    </Formik>
  );

  return (
    <div className="dark:bg-navy-900 min-h-screen">
      <div className="flex items-center justify-center h-[80vh]">
        {id ? (
          isLoading || !category ? (
            <Loader />
          ) : (
            renderForm(category)
          )
        ) : (
          renderForm({
            name: "",
            multiply: 1,
            urlImg: "",
            active: true,
            transportMode: "",
            vehicleTypeId: "",
          } as any)
        )}
      </div>
      <div className="ml-auto w-full px-4">
        <button
          onClick={() => {
            navigate("/admin/settings/categories");
          }}
          className="bg-primary text-white px-4 py-2 rounded w-[200px] hover:opacity-90 transition"
        >
          {t("back")}
        </button>
      </div>
    </div>
  );
}

export default CreateCategory;
